import { COLORS, SHADOWS } from './colors';

// Common component style utilities
export const createTextFieldStyles = (isDark: boolean) => {
  const primary = isDark ? COLORS.dark.primary : COLORS.light.primary;
  const primaryHover = isDark ? COLORS.dark.primaryHover : COLORS.light.primaryDark;
  
  return {
    root: {
      '& .MuiOutlinedInput-root': {
        borderRadius: "4px", 
        '& fieldset': {
          borderColor: primary,
          borderWidth: '2px'
        },
        '&:hover fieldset': {
          borderColor: primaryHover,
          borderWidth: '2px'
        },
        '&.Mui-focused fieldset': {
          borderColor: primary,
          borderWidth: '2px'
        },
        ...(isDark && {
          '&.Mui-disabled fieldset': {
            borderColor: 'rgba(100, 175, 227, 0.5) !important',
          }
        })
      },
      '& .MuiInputBase-root': {
        backgroundColor: isDark ? COLORS.dark.background.input : COLORS.light.background.paper,
        color: isDark ? COLORS.dark.text.primary : COLORS.light.text.primary,
        '& textarea, & input': {
          color: `${isDark ? COLORS.dark.text.primary : COLORS.light.text.primary} !important`,
          backgroundColor: 'transparent !important',
          ...(isDark && {
            '&::placeholder': {
              color: `${COLORS.dark.text.placeholder} !important`,
              opacity: 1,
            }
          })
        },
        '&.Mui-disabled': {
          color: isDark ? `${COLORS.dark.text.disabled} !important` : 'rgba(0, 0, 0, 0.6)',
          backgroundColor: isDark ? `${COLORS.dark.background.disabled} !important` : '#F5F5F5',
          '& textarea, & input': {
            color: isDark ? `${COLORS.dark.text.disabled} !important` : 'rgba(0, 0, 0, 0.6) !important',
            '-webkit-text-fill-color': isDark ? `${COLORS.dark.text.disabled} !important` : 'rgba(0, 0, 0, 0.6) !important',
          }
        }
      },
      '& .MuiInputLabel-root': {
        color: isDark ? COLORS.dark.primaryHover : primary,
        fontWeight: 500,
        '&.Mui-focused': {
          color: isDark ? primary : primaryHover
        },
        ...(isDark && {
          '&.Mui-disabled': {
            color: 'rgba(144, 202, 249, 0.7)',
          }
        })
      },
      ...(isDark && {
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: COLORS.dark.text.primary
        }
      }),
      // Style for datetime-local calendar icons
      ...(isDark && {
        '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
          filter: 'invert(1)',
          opacity: 0.8
        },
        '& input[type="datetime-local"]::-webkit-inner-spin-button': {
          filter: 'invert(1)',
          opacity: 0.8
        }
      })
    }
  };
};

export const createButtonStyles = (isDark: boolean) => {
  const baseButton = {
    borderRadius: isDark ? "4px" : "0px",
    ...(isDark && { fontWeight: 550 })
  };

  const disabledButton = isDark ? {
    background: 'linear-gradient(45deg, #1a1a1a 30%, #2a2a2a 90%)',
    color: '#666666',
    border: '1px solid #525252',
    opacity: 0.9,
    backdropFilter: 'blur(4px)',
    boxShadow: 'inset 0 0 5px rgba(0,0,0,0.2)',
    cursor: 'not-allowed',
    '& .MuiCircularProgress-root': {
      color: '#666666'
    },
    '& .MuiButton-startIcon': {
      color: '#666666'
    }
  } : {};

  return {
    root: {
      ...baseButton,
      '&.Mui-disabled': disabledButton,
      ...(!isDark && {
        '&:hover': {
          backgroundColor: "#e0e0e0"
        }
      })
    },
    contained: isDark ? {
      backgroundColor: COLORS.dark.primaryButton,
      color: "#000000",
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        backgroundColor: COLORS.dark.primaryLight,
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
      },
      '&.Mui-disabled': {
        background: 'linear-gradient(45deg, #1a1a1a 30%, #2a2a2a 90%)',
        color: '#666666',
        border: '1px solid #525252',
      }
    } : {},
    outlined: isDark ? {
      backgroundColor: 'transparent',
      color: COLORS.dark.primary,
      borderColor: COLORS.dark.primary,
      '&:hover': {
        backgroundColor: `rgba(100, 175, 227, 0.08)`,
        borderColor: COLORS.dark.primaryLight
      },
      '&.Mui-disabled': {
        background: 'transparent',
        color: '#666666',
        border: '1px solid #525252',
        opacity: 0.7,
      }
    } : {},
    text: isDark ? {
      backgroundColor: 'transparent',
      color: COLORS.dark.primary,
      '&:hover': {
        backgroundColor: `rgba(100, 175, 227, 0.08)`,
      },
    } : {}
  };
};

export const createAlertStyles = (isDark: boolean) => {
  const alertVariants = {
    warning: {
      light: {
        backgroundColor: '#FFF3E0',
        color: '#E65100',
        border: '1px solid #FF9800',
        iconColor: '#FF9800',
        messageColor: '#E65100'
      },
      dark: {
        backgroundColor: '#2D1B0E',
        color: '#FFB74D',
        border: '1px solid #FF9800',
        iconColor: '#FF9800',
        messageColor: '#FFFFFF'
      }
    },
    error: {
      light: {
        backgroundColor: '#FFEBEE',
        color: '#C62828',
        border: '1px solid #F44336',
        iconColor: '#F44336',
        messageColor: '#C62828'
      },
      dark: {
        backgroundColor: '#2D0E0E',
        color: '#EF5350',
        border: '1px solid #F44336',
        iconColor: '#F44336',
        messageColor: '#FFFFFF'
      }
    },
    info: {
      light: {
        backgroundColor: '#E3F2FD',
        color: '#1565C0',
        border: '1px solid #2196F3',
        iconColor: '#2196F3',
        messageColor: '#1565C0'
      },
      dark: {
        backgroundColor: '#0E1B2D',
        color: '#42A5F5',
        border: '1px solid #2196F3',
        iconColor: '#2196F3',
        messageColor: '#FFFFFF'
      }
    },
    success: {
      light: {
        backgroundColor: '#E8F5E8',
        color: '#2E7D32',
        border: '1px solid #4CAF50',
        iconColor: '#4CAF50',
        messageColor: '#2E7D32'
      },
      dark: {
        backgroundColor: '#0E2D0E',
        color: '#66BB6A',
        border: '1px solid #4CAF50',
        iconColor: '#4CAF50',
        messageColor: '#FFFFFF'
      }
    }
  };

  const theme = isDark ? 'dark' : 'light';
  
  return {
    root: {
      borderRadius: '8px',
      '&.MuiAlert-standardWarning': {
        backgroundColor: alertVariants.warning[theme].backgroundColor,
        color: alertVariants.warning[theme].color,
        border: alertVariants.warning[theme].border,
        '& .MuiAlert-icon': {
          color: alertVariants.warning[theme].iconColor,
        },
        '& .MuiAlert-message': {
          color: alertVariants.warning[theme].messageColor,
        },
      },
      '&.MuiAlert-standardError': {
        backgroundColor: alertVariants.error[theme].backgroundColor,
        color: alertVariants.error[theme].color,
        border: alertVariants.error[theme].border,
        '& .MuiAlert-icon': {
          color: alertVariants.error[theme].iconColor,
        },
        '& .MuiAlert-message': {
          color: alertVariants.error[theme].messageColor,
        },
      },
      '&.MuiAlert-standardInfo': {
        backgroundColor: alertVariants.info[theme].backgroundColor,
        color: alertVariants.info[theme].color,
        border: alertVariants.info[theme].border,
        '& .MuiAlert-icon': {
          color: alertVariants.info[theme].iconColor,
        },
        '& .MuiAlert-message': {
          color: alertVariants.info[theme].messageColor,
        },
      },
      '&.MuiAlert-standardSuccess': {
        backgroundColor: alertVariants.success[theme].backgroundColor,
        color: alertVariants.success[theme].color,
        border: alertVariants.success[theme].border,
        '& .MuiAlert-icon': {
          color: alertVariants.success[theme].iconColor,
        },
        '& .MuiAlert-message': {
          color: alertVariants.success[theme].messageColor,
        },
      },
    },
  };
};

export const createAccordionStyles = (isDark: boolean) => {
  const colors = isDark ? COLORS.dark : COLORS.light;
  
  return {
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: isDark ? "1px solid #525252" : "1px solid #e0e0e0",
          boxShadow: "none",
          '&:not(:last-child)': {
            borderBottom: 0,
          },
          '&:before': {
            display: 'none',
          },
          '&$expanded': {
            margin: 'auto',
          }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: isDark ? '#1E1E1E' : "#f5f5f5",
          borderBottom: isDark ? '1px solid rgba(255, 255, 255, .125)' : '1px solid rgba(0, 0, 0, .125)',
          marginBottom: -1,
          minHeight: 56,
          '&$expanded': {
            minHeight: 56,
          }
        },
        content: {
          '&$expanded': {
            margin: '12px 0',
          }
        },
        expandIconWrapper: {
          '&$expanded': {
            transform: 'rotate(90deg)',
          }
        }
      }
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        }
      }
    }
  };
};

export const createCommonComponents = (isDark: boolean) => {
  const colors = isDark ? COLORS.dark : COLORS.light;
  const shadows = isDark ? SHADOWS.dark : SHADOWS.light;
  
  return {
    MuiLink: {
      styleOverrides: {
        root: {
          color: isDark ? colors.primary : COLORS.light.primary,
          textDecoration: "none",
          display: 'inline-block',
          '&:hover': {
            textDecoration: "underline"
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: isDark ? colors.primary : COLORS.light.primary
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${colors.borders.primary}`,
        },
        elevation1: {
          boxShadow: shadows.elevation1,
        },
        elevation3: {
          boxShadow: shadows.elevation3,
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: isDark ? (colors.background.drawer || colors.background.paper) : colors.background.paper,
          borderRight: `1px solid ${colors.borders.primary}`,
          ...(isDark && {
            boxShadow: `2px 0 8px rgba(0, 0, 0, 0.3)`,
          })
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderColor: isDark ? colors.borders.secondary : colors.borders.secondary,
          '&:hover': {
            backgroundColor: isDark ? 'rgba(48, 54, 61, 0.5)' : '#f0f7ff',
            borderColor: isDark ? colors.borders.accent : 'rgba(25, 118, 210, 0.2)',
          },
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(48, 54, 61, 0.3)' : 'rgba(25, 118, 210, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: isDark ? 'rgba(88, 166, 255, 0.1)' : 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(88, 166, 255, 0.15)' : 'rgba(25, 118, 210, 0.12)',
            }
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: isDark ? colors.borders.primary : colors.borders.primary,
          backgroundColor: isDark ? colors.borders.primary : colors.borders.primary,
        }
      }
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            color: isDark ? colors.text.primary : COLORS.light.text.primary,
          },
          // Style for text-based icons (like ▲/▼)
          '& .MuiIconButton-root': {
            color: isDark ? colors.text.primary : COLORS.light.text.primary,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            }
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: isDark ? `1px solid ${colors.borders.primary} !important` : undefined,
        },
        head: {
          borderBottom: isDark ? `1px solid ${colors.borders.primary} !important` : undefined,
        },
        footer: {
          borderBottom: isDark ? `1px solid ${colors.borders.primary} !important` : undefined,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: isDark ? `1px solid ${colors.borders.primary} !important` : undefined,
        },
      },
    },
  };
}; 